import type { Channel, ChannelMediaItem, ChannelMediaKind } from '@novachat/shared';
import { Download, ExternalLink, FileText, MessageSquare, Play } from 'lucide-react';
import { useState } from 'react';
import { useChannelMedia } from '../lib/channel-media';
import { fileUrl, sizeText } from '../lib/upload';
import { jumpToMessage } from '../state/chat';
import { ChannelImageViewer } from './Attachments';
import { Modal } from './RoomDialogs';

const TABS: { kind: ChannelMediaKind; label: string; empty: string }[] = [
  { kind: 'media', label: 'Medya', empty: 'Bu kanalda henüz medya yok.' },
  { kind: 'files', label: 'Dosyalar', empty: 'Bu kanalda henüz dosya yok.' },
  {
    kind: 'links',
    label: 'Bağlantılar',
    empty: 'Bu kanalda henüz bağlantı yok.',
  },
];

const dateText = (iso: string) =>
  new Date(iso).toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
const byLine = (item: ChannelMediaItem) => `${item.author?.name ?? 'Silinmiş kullanıcı'} · ${dateText(item.createdAt)}`;

function JumpButton({ onJump, className = '' }: { onJump: () => void; className?: string }) {
  return (
    <button
      type="button"
      aria-label="Mesaja git"
      title="Mesaja git"
      onClick={onJump}
      className={`grid size-8 flex-none place-items-center rounded-sm border-0 text-fg-2 hover:bg-surface-3 hover:text-fg-1 ${className}`}
    >
      <MessageSquare size={15} />
    </button>
  );
}

/** "Medya, dosyalar ve bağlantılar": photos and videos as a grid, then files and links as lists, like Telegram's shared media. */
export function ChannelMediaDialog({ channel, onClose }: { channel: Channel; onClose: () => void }) {
  const [kind, setKind] = useState<ChannelMediaKind>('media');
  const media = useChannelMedia(channel.id, kind);
  const items = media.data?.pages.flatMap((page) => page.items) ?? [];
  const tab = TABS.find((t) => t.kind === kind)!;
  // A photo opens in the shared viewer, which goes through all photos of the channel and loads older ones.
  const [viewing, setViewing] = useState<ChannelMediaItem | null>(null);

  return (
    <>
      {/* One size for every tab, whatever it holds; the tab's content scrolls inside. */}
      <Modal
        title="Medya, dosyalar ve bağlantılar"
        onClose={onClose}
        size="flex h-[min(760px,calc(100dvh-96px))] max-h-none w-[min(860px,calc(100vw-32px))] max-w-none flex-col [&:not([open])]:hidden"
      >
        {(close) => {
          const jump = (item: ChannelMediaItem) => {
            jumpToMessage(channel.id, item.messageId);
            close();
          };
          return (
            <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
              <div role="tablist" aria-label="Paylaşılanlar" className="grid grid-cols-3 gap-1 rounded-md bg-surface-2 p-1">
                {TABS.map((t) => (
                  <button
                    key={t.kind}
                    type="button"
                    role="tab"
                    aria-selected={t.kind === kind}
                    onClick={() => setKind(t.kind)}
                    className={`min-w-0 truncate rounded-sm border-0 px-2 py-1.5 text-[13px] font-semibold ${t.kind === kind ? 'bg-surface-3 text-fg-1' : 'bg-transparent text-fg-3 hover:text-fg-1'}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              <div role="tabpanel" aria-label={tab.label} className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto">
                {media.isPending && <p className="m-0 text-sm text-fg-3">Yükleniyor…</p>}
                {media.isError && <p className="m-0 text-sm text-[#EE9191]">Yüklenemedi. Biraz sonra yeniden dene.</p>}
                {media.isSuccess && items.length === 0 && <p className="m-0 text-sm text-fg-3">{tab.empty}</p>}

                {kind === 'media' && items.length > 0 && (
                  <div className="grid grid-cols-3 gap-1 sm:grid-cols-4">
                    {items.map((item) => (
                      <MediaTile key={item.key} item={item} onJump={() => jump(item)} onView={() => setViewing(item)} />
                    ))}
                  </div>
                )}

                {kind === 'files' && (
                  <ul className="m-0 flex list-none flex-col gap-1 p-0">
                    {items.map((item) => (
                      <li key={item.key} className="flex min-w-0 items-center gap-2 rounded-md border border-line bg-surface-2 py-1.5 pr-1.5 pl-3">
                        <FileText size={20} className="flex-none text-fg-3" />
                        <a
                          href={fileUrl(item.attachment!.url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          download={item.attachment!.fileName}
                          className="min-w-0 flex-1 text-fg-1 no-underline"
                        >
                          <span className="block truncate text-sm font-semibold">{item.attachment!.fileName}</span>
                          <span className="block truncate text-xs text-fg-3">
                            {sizeText(item.attachment!.size)} · {byLine(item)}
                          </span>
                        </a>
                        <a
                          href={fileUrl(item.attachment!.url)}
                          download={item.attachment!.fileName}
                          aria-label="İndir"
                          title="İndir"
                          className="grid size-8 flex-none place-items-center rounded-sm text-fg-2 hover:bg-surface-3 hover:text-fg-1"
                        >
                          <Download size={15} />
                        </a>
                        <JumpButton onJump={() => jump(item)} className="bg-transparent" />
                      </li>
                    ))}
                  </ul>
                )}

                {kind === 'links' && (
                  <ul className="m-0 flex list-none flex-col gap-1 p-0">
                    {items.map((item) => {
                      const { url, preview } = item.link!;
                      return (
                        <li key={item.key} className="flex min-w-0 items-center gap-2 rounded-md border border-line bg-surface-2 py-1.5 pr-1.5 pl-3">
                          <ExternalLink size={18} className="flex-none text-fg-3" />
                          <a href={url} target="_blank" rel="noopener noreferrer" className="min-w-0 flex-1 no-underline">
                            {preview && <span className="block truncate text-sm font-semibold text-fg-1">{preview.title}</span>}
                            <span className="block truncate text-[13px] text-teal-400">{url}</span>
                            <span className="block truncate text-xs text-fg-3">{byLine(item)}</span>
                          </a>
                          <JumpButton onJump={() => jump(item)} className="bg-transparent" />
                        </li>
                      );
                    })}
                  </ul>
                )}

                {media.hasNextPage && (
                  <button
                    type="button"
                    disabled={media.isFetchingNextPage}
                    onClick={() => void media.fetchNextPage()}
                    className="mt-2 w-full rounded-md border border-line bg-transparent px-3 py-2 text-[13px] font-semibold text-fg-2 hover:bg-surface-2 disabled:opacity-60"
                  >
                    {media.isFetchingNextPage ? 'Yükleniyor…' : 'Daha fazla'}
                  </button>
                )}
              </div>
            </div>
          );
        }}
      </Modal>
      {viewing?.attachment && (
        <ChannelImageViewer channelId={channel.id} messageId={viewing.messageId} start={viewing.attachment} own={[viewing.attachment]} onClose={() => setViewing(null)} />
      )}
    </>
  );
}

/** A square thumbnail: a photo opens in the viewer, a video in a new tab; the corner button jumps to its message. */
function MediaTile({ item, onJump, onView }: { item: ChannelMediaItem; onJump: () => void; onView: () => void }) {
  const attachment = item.attachment!;
  const thumb = attachment.previewUrl ?? (attachment.kind === 'image' ? attachment.url : null);
  const title = `${attachment.fileName} · ${byLine(item)}`;
  const content = (
    <>
      {thumb ? (
        <img src={fileUrl(thumb)} alt={attachment.fileName} loading="lazy" className="size-full object-cover" />
      ) : (
        <span className="grid size-full place-items-center text-fg-3">
          <Play size={24} />
        </span>
      )}
      {attachment.kind === 'video' && thumb && (
        <span className="absolute inset-0 grid place-items-center">
          <Play size={24} className="rounded-full bg-black/55 p-1 text-white" />
        </span>
      )}
    </>
  );
  return (
    <div className="relative aspect-square min-w-0 overflow-hidden rounded-sm bg-surface-3">
      {attachment.kind === 'image' ? (
        <button type="button" aria-label={attachment.fileName} title={title} onClick={onView} className="block size-full cursor-zoom-in border-0 bg-transparent p-0">
          {content}
        </button>
      ) : (
        <a href={fileUrl(attachment.url)} target="_blank" rel="noopener noreferrer" aria-label={attachment.fileName} title={title} className="block size-full">
          {content}
        </a>
      )}
      <JumpButton onJump={onJump} className="absolute right-1 bottom-1 size-7 bg-black/55 text-white hover:bg-black/75" />
    </div>
  );
}
